import { useState } from "react"

import { AgentMessageType } from "@vertesia/common"
import { CopyIcon } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { DownloadAsDocxButton } from "@components/buttons/DownloadAsDocxButton"
import { DownloadAsPdfButton } from "@components/buttons/DownloadAsPdfButton"
import { DownloadContentButton } from "@components/buttons/DownloadContentButton"
import { MarkdownDisplay } from "@components/displays/MarkdownDisplay"
import { SaveDocumentModal } from "@components/modals/SaveDocumentModal"

import { getWorkstreamId } from "@lib/agent"

import { AgentMessage } from "@models/agents"

interface MessageItemProps {
    message: AgentMessage
    showDetailsOption?: boolean
}
export function MessageItem({ message, showDetailsOption = false }: Readonly<MessageItemProps>) {
    const [showDetails, setShowDetails] = useState(false)

    const getMessageContent = () => {
        let content = ""

        if (message.message) {
            if (typeof message.message === "object") {
                content = JSON.stringify(message.message, null, 2)
            } else if (message.message.trim) {
                content = message.message.trim()
            } else {
                content = String(message.message)
            }
        }

        return content
    }

    const getMessageContentForUse = () => {
        const messageContent = getMessageContent() || ""
        const detailsContent = typeof message.details === "string" ? message.details : message.details ? JSON.stringify(message.details, null, 2) : ""

        return [messageContent, detailsContent ? "\n\nDetails:\n" + detailsContent : ""].join("").trim()
    }

    const copyToClipboard = () => {
        const textToCopy = getMessageContentForUse()

        navigator.clipboard.writeText(textToCopy).then(() => {
            toast.success("Copied to clipboard", {
                description: "Message content copied to clipboard",
                duration: 2000,
            })
        })
    }

    const renderContent = (content: string | object) => {
        if (typeof content === "object") {
            return <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto bg-gray-100 p-2 rounded text-gray-700">{JSON.stringify(content, null, 2)}</pre>
        }

        return (
            <div className="prose prose-sm max-w-none prose-p:my-1 prose-pre:bg-gray-800 prose-pre:my-2 prose-headings:my-2">
                <MarkdownDisplay content={content} />
            </div>
        )
    }

    const messageContent = getMessageContent()
    const workstreamId = getWorkstreamId(message)

    return (
        <div className={`flex flex-row ${message.type === AgentMessageType.QUESTION ? "justify-end" : "justify-start"} overflow-hidden`}>
            <div className={`flex flex-col p-3 gap-1 text-sm ${message.type === AgentMessageType.QUESTION ? "bg-muted text-foreground rounded-lg" : "w-full"} overflow-hidden`} data-workstream-id={workstreamId}>
                {messageContent && <div className="message-content">{renderContent(messageContent)}</div>}
                {message.details && showDetailsOption && (
                    <div className="mt-2">
                        <button onClick={() => setShowDetails(!showDetails)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center">
                            {showDetails ? "Hide" : "Show"} details
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 ml-1 transition-transform ${showDetails ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {showDetails && <div className="mt-2 p-2 bg-gray-50 border border-gray-100 rounded text-sm">{typeof message.details === "string" ? renderContent(message.details) : <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto bg-gray-100 p-2 rounded text-gray-700">{JSON.stringify(message.details, null, 2)}</pre>}</div>}
                    </div>
                )}
                <div className={`flex items-center text-xs text-gray-400 gap-1 ${message.type === AgentMessageType.QUESTION ? "justify-end text-right" : "justify-start text-left"}`}>
                    <span>{formatedTimestamp(message.timestamp)}</span>
                    {message.type !== AgentMessageType.QUESTION && (
                        <div className="flex items-center gap-2">
                            <SaveDocumentModal content={getMessageContentForUse()} variant="ghost" size="xs" />
                            <DownloadContentButton content={getMessageContentForUse()} variant="ghost" size="xs" />
                            <DownloadAsDocxButton content={getMessageContentForUse()} variant="ghost" size="xs" hideText />
                            <DownloadAsPdfButton content={getMessageContentForUse()} variant="ghost" size="xs" hideText />
                            <ActionTooltipButton icon={<CopyIcon />} variant="ghost" size="xs" tooltip="Copy to clipboard" onClick={copyToClipboard} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function formatedTimestamp(timestamp: number): string {
    const date = new Date(timestamp)
    return `${date.toLocaleDateString()} - ${date.toLocaleTimeString()}`
}
